import {
  AttachementFileType,
  ISitePermitFilterInput,
  PermitFieldType,
  SitePermitStatus,
} from '~/client/graph'

import {
  FILTER_TYPE_TO_PERMIT_FIELD_TYPE,
  LOCATION_TAG_TYPES,
} from '../../../constants/FilterTypeMappings'
import { FilterType } from '../../../enums/FilterType'
import { TagType } from '../../../enums/TagType'
import Workflow from '../../../models/Workflow'
import TagsStore from '../../../stores/domain/Tags.store'
import WorkflowTypesStore from '../../../stores/domain/WorkflowTypes.store'
import {
  getFilterTypeFromUnassignedId,
  isUnassignedId,
} from '../components/WorkflowFilters/useWorkflowFilterCategories'
import {
  FILE_TYPE_CATEGORY,
  PROJECT_CATEGORY,
} from '../components/WorkflowFilters/WorkflowFilters.types'

/**
 * Map FilterType to TagType category key for grouping filters by category.
 */
export const FILTER_TYPE_TO_CATEGORY: Partial<Record<FilterType, string>> = {
  [FilterType.Category]: TagType.FormCategory,
  [FilterType.Type]: TagType.PermitType,
  [FilterType.Status]: TagType.Status,
  [FilterType.Location]: TagType.Area,
  [FilterType.Equipment]: TagType.OffloadingEquipment,
  [FilterType.Company]: TagType.Company,
  [FilterType.ResponsibleContact]: TagType.User,
  [FilterType.Attachment]: FILE_TYPE_CATEGORY,
  [FilterType.Project]: PROJECT_CATEGORY,
}

const FILE_TYPE_PREFIX = 'filetype_'

/**
 * Extract AttachementFileType from a file type tag ID.
 * Handles case-insensitive matching against the enum values.
 */
function getAttachmentFileTypeFromTagId(
  tagId: string,
): AttachementFileType | null {
  if (!tagId.toLowerCase().startsWith(FILE_TYPE_PREFIX.toLowerCase())) {
    return null
  }

  const fileTypeName = tagId.substring(FILE_TYPE_PREFIX.length)
  const fileTypeEnumValues = Object.values(AttachementFileType)

  const matchedType = fileTypeEnumValues.find(
    enumValue => enumValue.toLowerCase() === fileTypeName.toLowerCase(),
  )

  return matchedType || null
}

/**
 * Group selected filter tag IDs by their category.
 * Handles special cases like Unassigned IDs, file types, status, and projects.
 */
export function groupFiltersByCategory(
  selectedFilterTagIds: string[],
  tagsStore: TagsStore | undefined,
  projectIdSet: Set<string>,
  filterTypeToCategory: Partial<Record<FilterType, string>>,
): Record<string, string[]> {
  if (selectedFilterTagIds.length === 0) return {}

  const formStatusValues = Object.values(SitePermitStatus) as string[]

  return selectedFilterTagIds.reduce(
    (acc, tagId) => {
      if (isUnassignedId(tagId)) {
        const filterType = getFilterTypeFromUnassignedId(tagId)
        if (filterType) {
          const category = filterTypeToCategory[filterType]
          if (category) {
            if (!acc[category]) acc[category] = []
            acc[category].push(tagId)
          }
        }
        return acc
      }

      if (tagId.startsWith(FILE_TYPE_PREFIX)) {
        const category = FILE_TYPE_CATEGORY
        if (!acc[category]) acc[category] = []
        acc[category].push(tagId)
        return acc
      }

      if (formStatusValues.includes(tagId)) {
        const category = TagType.Status as string
        if (!acc[category]) acc[category] = []
        acc[category].push(tagId)
        return acc
      }

      if (projectIdSet.has(tagId)) {
        const category = PROJECT_CATEGORY
        if (!acc[category]) acc[category] = []
        acc[category].push(tagId)
        return acc
      }

      const tag = tagsStore?.getTagById(tagId)
      if (!tag) return acc

      let category = tag.type as string
      if (LOCATION_TAG_TYPES.includes(tag.type as TagType)) {
        category = TagType.Area
      }

      if (!acc[category]) acc[category] = []
      acc[category].push(tagId)
      return acc
    },
    {} as Record<string, string[]>,
  )
}

/**
 * Find filter types that have ONLY Unassigned selected.
 * Backend doesn't support "only unassigned" filtering, so these need client-side filtering.
 */
export function getOnlyUnassignedFilterTypes(
  filtersByCategory: Record<string, string[]>,
  filterTypeToCategory: Partial<Record<FilterType, string>>,
): FilterType[] {
  const result: FilterType[] = []

  Object.entries(filtersByCategory).forEach(([categoryKey, ids]) => {
    if (!ids || ids.length === 0) return

    const hasOnlyUnassigned = ids.every(id => isUnassignedId(id))
    if (hasOnlyUnassigned) {
      const filterType = Object.entries(filterTypeToCategory).find(
        ([, cat]) => cat === categoryKey,
      )?.[0] as FilterType | undefined

      if (filterType) {
        result.push(filterType)
      }
    }
  })

  return result
}

/**
 * Build server-side filter from grouped filters.
 * Converts category-grouped tag IDs to the ISitePermitFilterInput format.
 */
export function buildServerFilter(
  filtersByCategory: Record<string, string[]>,
  workflowTypesStore?: WorkflowTypesStore,
): ISitePermitFilterInput {
  const getFilteredIds = (ids: string[] | undefined): string[] => {
    return ids?.filter(id => !isUnassignedId(id)) || []
  }

  const unassignedFields: PermitFieldType[] = []
  Object.values(filtersByCategory).forEach(ids => {
    const nonUnassignedIds = ids?.filter(id => !isUnassignedId(id)) || []
    if (nonUnassignedIds.length > 0) {
      ids?.forEach(id => {
        if (isUnassignedId(id)) {
          const filterType = getFilterTypeFromUnassignedId(id)
          if (filterType) {
            const fieldType = FILTER_TYPE_TO_PERMIT_FIELD_TYPE[filterType]
            if (fieldType) {
              unassignedFields.push(fieldType)
            }
          }
        }
      })
    }
  })

  const getTypeFilter = (): Partial<ISitePermitFilterInput> => {
    const filteredIds = getFilteredIds(filtersByCategory[TagType.PermitType])
    if (!filteredIds.length) return {}

    const initialTypes = workflowTypesStore
      ? Array.from(
          new Set(
            filteredIds
              .map(typeId => workflowTypesStore.getPermitTypeById(typeId)?.type)
              .filter(Boolean),
          ),
        )
      : []

    if (initialTypes.length) {
      return { initialTypes }
    }

    return { typeIds: filteredIds }
  }

  const companyIds = getFilteredIds(filtersByCategory[TagType.Company])
  const assigneeIds = getFilteredIds(filtersByCategory[TagType.User])
  const locationIds = getFilteredIds(filtersByCategory[TagType.Area])
  const equipmentIds = getFilteredIds(
    filtersByCategory[TagType.OffloadingEquipment],
  )
  const fileTagIds = getFilteredIds(filtersByCategory[FILE_TYPE_CATEGORY])
  const projectIds = getFilteredIds(filtersByCategory[PROJECT_CATEGORY])

  const result: ISitePermitFilterInput = {
    ...(getFilteredIds(filtersByCategory[TagType.FormCategory]).length && {
      categoryIds: getFilteredIds(filtersByCategory[TagType.FormCategory]),
    }),
    ...getTypeFilter(),
    ...(getFilteredIds(filtersByCategory[TagType.Status]).length && {
      statuses: getFilteredIds(
        filtersByCategory[TagType.Status],
      ) as SitePermitStatus[],
    }),
    ...(companyIds.length && { companyIds }),
    ...(assigneeIds.length && { assigneeIds }),
    ...(locationIds.length && { locationIds }),
    ...(equipmentIds.length && { equipmentIds }),
    ...(fileTagIds.length && {
      files: fileTagIds
        .map(getAttachmentFileTypeFromTagId)
        .filter((v): v is AttachementFileType => v !== null),
    }),
    ...(projectIds.length && { projectId: projectIds[0] }),
    ...(unassignedFields.length && {
      includeUnassignedFields: unassignedFields,
    }),
  }

  return result
}

/**
 * Filter workflows client-side for "only Unassigned" selections.
 * Backend doesn't support this, so we filter after receiving data.
 */
export function filterWorkflowsByUnassigned(
  workflows: Workflow[],
  onlyUnassignedFilterTypes: FilterType[],
): Workflow[] {
  if (onlyUnassignedFilterTypes.length === 0) {
    return workflows
  }

  return workflows.filter(workflow => {
    for (const filterType of onlyUnassignedFilterTypes) {
      switch (filterType) {
        case FilterType.Category:
          if (workflow.categoryId) return false
          break
        case FilterType.Type:
          if (workflow.typeId) return false
          break
        case FilterType.Status:
          if (workflow.status) return false
          break
        case FilterType.Company:
          if (workflow.companyIds?.length > 0) return false
          break
        case FilterType.ResponsibleContact:
          if (workflow.responsibleIds?.length > 0) return false
          break
        case FilterType.Location:
          if (workflow.locations?.length > 0) return false
          break
        case FilterType.Equipment:
          if (workflow.equipment?.length > 0) return false
          break
        case FilterType.Attachment:
          if (workflow.attachments?.length > 0) return false
          break
        case FilterType.Project:
          if (workflow.projectId) return false
          break
      }
    }
    return true
  })
}

/**
 * Merge filter arrays - combines instead of overwriting.
 */
export function mergeFilterArrays<T>(
  existing: T[] | undefined,
  newValue: T,
): T[] {
  if (!existing || existing.length === 0) return [newValue]
  if (existing.includes(newValue)) return existing
  return [...existing, newValue]
}
